import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, MapPin, ShieldCheck } from "lucide-react";
import { MaskedLine, FadeUp, ParallaxImage, Page, Meta, EASE } from "../components/Reveal";
import { getAvailability } from "../lib/api";
import { useLinks } from "../lib/LinksContext";
import { useContent } from "../lib/ContentContext";

const ARCH = "rounded-t-[999px] rounded-b-[2rem]";

const toList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function FaqItem({ q, a, link, linkLabel, open, onClick, i }) {
  return (
    <div className="border-b border-linen">
      <button onClick={onClick} data-testid={`faq-q-${i}`}
        className="flex w-full items-center justify-between gap-6 py-6 text-left">
        <span className="font-serif text-xl font-light tracking-tight md:text-2xl">{q}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-clay transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: EASE }} className="overflow-hidden">
            <p className="max-w-2xl pb-4 text-sm leading-relaxed text-smoke md:text-base">{a}</p>
            {link && (
              <a href="mailto:contact@eugenielarrivee.com?subject=Tarifs%20et%20modalit%C3%A9s"
                data-testid="faq-tarifs-link"
                className="group mb-7 inline-flex items-center gap-2 border-b border-charcoal/30 pb-1 text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:border-clay hover:text-clay">
                {linkLabel}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Clinique() {
  const c = useContent();
  const [availability, setAvailability] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [showFormations, setShowFormations] = useState(false);
  const links = useLinks();
  const sexualisUrl = links.sexualis || "#";

  useEffect(() => {
    getAvailability().then(setAvailability).catch(() => setAvailability({ open: false, note: "" }));
  }, []);

  const MOTIFS = [1, 2, 3, 4, 5, 6].map((n) => ({
    title: c(`clinique.motif.${n}.titre`),
    items: toList(c(`clinique.motif.${n}.items`)),
  }));

  const FORMATS = [1, 2].map((n) => ({
    title: c(`clinique.format.${n}.titre`),
    text: c(`clinique.format.${n}.texte`),
  }));

  const STEPS = [1, 2, 3].map((n) => ({
    n: String(n).padStart(2, "0"),
    title: c(`clinique.etape.${n}.titre`),
    sub: c(`clinique.etape.${n}.sub`),
    text: c(`clinique.etape.${n}.texte`),
  }));

  const APPROACHES = [1, 2, 3].map((n) => ({
    title: c(`clinique.approches.${n}.titre`),
    text: c(`clinique.approches.${n}.texte`),
  }));

  const FAQ = [1, 2, 3, 4, 5, 6, 7].map((n) => ({
    q: c(`clinique.faq.${n}.q`),
    a: c(`clinique.faq.${n}.a`),
    link: n === 7,
  }));

  const FORMATIONS = toList(c("clinique.formation.liste"));
  const ENCADREMENT = toList(c("clinique.encadrement.liste"));

  return (
    <Page testId="clinique-page">
      {/* 1. HERO */}
      <section className="relative overflow-hidden mx-auto max-w-7xl px-6 pt-36 md:px-12 md:pt-48">
        <div className="pointer-events-none absolute -top-10 right-[-8rem] h-[24rem] w-[24rem] rounded-full bg-sage/50 blur-3xl" aria-hidden="true" />
        <div className="relative grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp><Meta>{c("clinique.hero.meta")}</Meta></FadeUp>
            <h1 className="mt-6 font-serif text-4xl font-light leading-[1.1] tracking-tight md:text-6xl lg:text-7xl">
              <MaskedLine delay={0.2}>{c("clinique.hero.l1")}</MaskedLine>
              <MaskedLine delay={0.35} className="italic text-clay">{c("clinique.hero.l2")}</MaskedLine>
            </h1>
            <FadeUp delay={0.6} className="mt-10 max-w-xl space-y-5">
              <p className="text-base leading-relaxed text-smoke md:text-lg">
                {c("clinique.hero.p1")}
              </p>
              <p className="text-base leading-relaxed text-smoke md:text-lg">
                {c("clinique.hero.p2")}
              </p>
            </FadeUp>
            <FadeUp delay={0.8} className="mt-10">
              <button
                data-testid="clinique-cta-fonctionnement"
                onClick={() => document.getElementById("fonctionnement")?.scrollIntoView({ behavior: "smooth" })}
                className="group inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
                {c("clinique.hero.cta")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              {availability && (
                <p data-testid="clinique-availability-note" className="mt-7 inline-flex items-center gap-2.5 text-xs uppercase tracking-[0.18em] text-ash">
                  <span className={`h-2 w-2 rounded-full ${availability.open ? "bg-moss" : "bg-clay"}`} />
                  {availability.open ? c("clinique.dispo.ouverte") : c("clinique.dispo.pause")}
                </p>
              )}
            </FadeUp>
          </div>
          <FadeUp className="lg:col-span-5" y={40} delay={0.4}>
            <div className="relative mx-auto max-w-sm">
              <div className={`absolute -bottom-6 -right-6 h-full w-full bg-sage/50 ${ARCH}`} aria-hidden="true" />
              <ParallaxImage src={c("clinique.hero.img")} alt="Eugénie Larrivée dans son bureau" className={`relative aspect-[4/5] ${ARCH}`} speed={6} position="50% 18%" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* 2. POURQUOI CONSULTER */}
      <section data-testid="clinique-motifs-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <FadeUp>
          <h2 className="max-w-3xl font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
            {c("clinique.motifs.t1")} <em className="text-clay">{c("clinique.motifs.t2")}</em>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-smoke md:text-lg">
            {c("clinique.motifs.p")}
          </p>
        </FadeUp>
        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {MOTIFS.map((m, i) => (
            <FadeUp key={m.title} delay={0.06 * i} className="h-full">
              <div data-testid={`motif-card-${i + 1}`} className="flex h-full flex-col rounded-[1.75rem] border border-linen bg-shell p-8 transition-colors duration-500 hover:bg-sand/20">
                <span className="font-serif text-lg italic text-clay/70">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-serif text-2xl font-light tracking-tight">{m.title}</h3>
                <ul className="mt-5 flex-1 space-y-2">
                  {m.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-smoke">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-clay/60" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <p className="mt-10 font-serif text-lg font-light italic text-smoke md:text-xl">
            {c("clinique.motifs.note")}
          </p>
        </FadeUp>
      </section>

      {/* 3. INDIVIDUEL OU COUPLE */}
      <section data-testid="clinique-formats-section" className="bg-sage/30 py-24 md:py-36">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <FadeUp>
            <h2 className="font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
              {c("clinique.formats.t1")} <em className="text-clay">{c("clinique.formats.t2")}</em>
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-8 md:grid-cols-2">
            {FORMATS.map((f, i) => (
              <FadeUp key={f.title} delay={0.1 * i} className="h-full">
                <div data-testid={`format-card-${i + 1}`} className={`flex h-full flex-col rounded-[2rem] p-10 md:p-12 ${i === 0 ? "bg-cream" : "bg-sand/40"}`}>
                  <h3 className="font-serif text-3xl font-light tracking-tight">{f.title}</h3>
                  <p className="mt-6 flex-1 text-base leading-relaxed text-smoke">{f.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* 4. COMMENT ÇA FONCTIONNE */}
      <section id="fonctionnement" data-testid="clinique-fonctionnement-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <FadeUp><Meta>{c("clinique.fonctionnement.meta")}</Meta></FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
            {c("clinique.fonctionnement.t1")} <em className="text-clay">{c("clinique.fonctionnement.t2")}</em>
          </h2>
        </FadeUp>
        <div className="mt-16 grid gap-10 md:grid-cols-3 md:gap-8">
          {STEPS.map((s, i) => (
            <FadeUp key={s.n} delay={0.1 * i}>
              <div data-testid={`step-${s.n}`} className="border-t border-linen pt-8">
                <span className="font-serif text-6xl font-light text-clay/30 md:text-7xl">{s.n}</span>
                <h3 className="mt-6 font-serif text-2xl font-light tracking-tight md:text-3xl">{s.title}</h3>
                <p className="mt-1 text-xs font-medium uppercase tracking-[0.2em] text-ash">{s.sub}</p>
                <p className="mt-5 text-sm leading-relaxed text-smoke md:text-base">{s.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.25}>
          <p className="mt-14 max-w-2xl border-l-2 border-clay pl-6 font-serif text-lg font-light italic leading-relaxed text-smoke md:text-xl">
            {c("clinique.fonctionnement.note")}
          </p>
        </FadeUp>
      </section>

      {/* 5. MA FAÇON DE TRAVAILLER */}
      <section data-testid="clinique-approche-section" className="bg-shell py-24 md:py-36">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <FadeUp><Meta>{c("clinique.approche.meta")}</Meta></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
              {c("clinique.approche.t1")} <em className="text-clay">{c("clinique.approche.t2")}</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-10 space-y-6">
            <p className="text-base leading-relaxed text-smoke md:text-lg">
              {c("clinique.approche.p1")}
            </p>
            <p className="text-base leading-relaxed text-smoke md:text-lg">
              {c("clinique.approche.p2")}
            </p>
            <p className="font-serif text-xl font-light italic text-charcoal md:text-2xl">
              {c("clinique.approche.citation")}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* 6. APPROCHES CLINIQUES */}
      <section data-testid="clinique-approches-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <FadeUp>
          <h2 className="font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
            {c("clinique.approches.t1")} <em className="text-clay">{c("clinique.approches.t2")}</em>
          </h2>
        </FadeUp>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {APPROACHES.map((a, i) => (
            <FadeUp key={a.title} delay={0.08 * i} className="h-full">
              <div data-testid={`approche-card-${i + 1}`} className="flex h-full flex-col rounded-[1.75rem] border border-linen bg-shell p-9">
                <h3 className="font-serif text-2xl font-light leading-snug tracking-tight">{a.title}</h3>
                <p className="mt-5 flex-1 text-sm leading-relaxed text-smoke md:text-base">{a.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <p className="mt-10 max-w-3xl font-serif text-lg font-light italic text-smoke md:text-xl">
            {c("clinique.approches.note")}
          </p>
        </FadeUp>
      </section>

      {/* 7. VISION GLOBALE — section éditoriale */}
      <section data-testid="clinique-vision-section" className="bg-charcoal py-28 text-cream md:py-40">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <FadeUp>
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-cream/50">{c("clinique.vision.meta")}</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-8 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-6xl">
              {c("clinique.vision.t1")} <em className="text-claylight">{c("clinique.vision.t2")}</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-10 max-w-2xl text-base leading-relaxed text-cream/80 md:text-lg">
              {c("clinique.vision.p")}
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <blockquote className="mt-16 border-l-2 border-claylight pl-8 font-serif text-2xl font-light italic leading-relaxed text-claylight md:text-4xl">
              {c("clinique.vision.citation")}
            </blockquote>
          </FadeUp>
        </div>
      </section>

      {/* 8. LE BUREAU */}
      <section data-testid="clinique-bureau-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <div className="grid items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp><Meta>{c("clinique.bureau.meta")}</Meta></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
                {c("clinique.bureau.t1")} <em className="text-clay">{c("clinique.bureau.t2")}</em>
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-8 flex max-w-xl items-start gap-3 text-base leading-relaxed text-smoke md:text-lg">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-clay" strokeWidth={1.5} />
                <span>
                  <strong className="font-medium text-charcoal">{c("clinique.bureau.adresse.titre")}</strong>
                  <br />
                  {c("clinique.bureau.adresse.texte")}
                </span>
              </p>
              <p className="mt-4 max-w-xl text-xs italic text-ash">
                {c("clinique.bureau.note")}
              </p>
            </FadeUp>
          </div>
        </div>
        <div className="mt-14 grid items-start gap-8 md:grid-cols-12">
          <FadeUp className="md:col-span-7" y={40}>
            <div data-testid="office-photo-1">
              <div className="overflow-hidden rounded-[2rem]">
                <img src={c("clinique.bureau.photo.1.img")} alt={c("clinique.bureau.photo.1.label")}
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>
              <p className="mt-3 text-center text-xs italic text-ash">{c("clinique.bureau.photo.1.label")}</p>
            </div>
          </FadeUp>
          <FadeUp className="md:col-span-5" y={40} delay={0.12}>
            <div data-testid="office-video">
              <div className={`overflow-hidden ${ARCH}`}>
                <video src={c("clinique.bureau.video")} poster={c("clinique.bureau.photo.1.img")}
                  autoPlay muted loop playsInline preload="auto"
                  className="aspect-[3/4] w-full object-cover" />
              </div>
              <p className="mt-3 text-center text-xs italic text-ash">{c("clinique.bureau.video.label")}</p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* 9. INFORMATIONS PRATIQUES — FAQ */}
      <section data-testid="clinique-faq-section" className="bg-shell py-24 md:py-36">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <FadeUp><Meta>{c("clinique.faq.meta")}</Meta></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
              {c("clinique.faq.t1")} <em className="text-clay">{c("clinique.faq.t2")}</em>
            </h2>
          </FadeUp>
          <div className="mt-14">
            {FAQ.map((f, i) => (
              <FaqItem key={f.q} q={f.q} a={f.a} link={f.link} linkLabel={c("clinique.faq.tarifs.lien")} i={i + 1}
                open={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
          <FadeUp delay={0.15}>
            <p className="mt-8 text-xs italic text-ash">
              {c("clinique.faq.note")}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* 10. FORMATION CONTINUE */}
      <section data-testid="clinique-formation-section" className="mx-auto max-w-4xl px-6 py-24 md:px-12 md:py-32">
        <FadeUp><Meta>{c("clinique.formation.meta")}</Meta></FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
            {c("clinique.formation.t1")} <em className="text-clay">{c("clinique.formation.t2")}</em>
          </h2>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-smoke md:text-lg">
            {c("clinique.formation.p")}
          </p>
          <button
            data-testid="formations-toggle"
            onClick={() => setShowFormations((v) => !v)}
            className="group mt-8 inline-flex items-center gap-2 border-b border-charcoal/30 pb-1 text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 hover:border-clay hover:text-clay">
            {showFormations ? "Masquer les formations" : "Voir mes formations complémentaires"}
            <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${showFormations ? "rotate-180" : ""}`} />
          </button>
        </FadeUp>
        <AnimatePresence initial={false}>
          {showFormations && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} className="overflow-hidden">
              <ul data-testid="formations-list" className="mt-8 space-y-0">
                {FORMATIONS.map((f, i) => (
                  <li key={f} className="flex items-baseline gap-5 border-b border-linen py-4">
                    <span className="font-serif text-sm italic text-clay/70">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm leading-relaxed text-smoke md:text-base">{f}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs italic text-ash">{c("clinique.formation.note")}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* 11. ENCADREMENT PROFESSIONNEL */}
      <section data-testid="clinique-encadrement-section" className="bg-sage/30 py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <FadeUp>
            <div className="flex items-start gap-5">
              <ShieldCheck className="mt-1 h-7 w-7 shrink-0 text-moss" strokeWidth={1.25} />
              <div>
                <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">
                  {c("clinique.encadrement.titre")}
                </h2>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {ENCADREMENT.map((e) => (
                    <li key={e} className="flex items-start gap-2.5 text-sm leading-relaxed text-smoke">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-clay" />
                      {e}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs italic text-ash">
                  {c("clinique.encadrement.note")}
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* 12. DISPONIBILITÉS */}
      <section data-testid="clinique-dispo-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <FadeUp>
          <div className="mx-auto max-w-3xl rounded-[2rem] border border-linen bg-shell p-12 text-center md:p-16">
            {availability?.open ? (
              <>
                <Meta className="text-moss">{c("clinique.dispo.meta")}</Meta>
                <h2 className="mt-6 font-serif text-3xl font-light leading-snug tracking-tight md:text-4xl">
                  {c("clinique.dispo.ouvert.t1")} <em className="text-clay">{c("clinique.dispo.ouvert.t2")}</em> {c("clinique.dispo.ouvert.t3")}
                </h2>
                {availability.note && <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-smoke">{availability.note}</p>}
                <a href={sexualisUrl} {...(links.sexualis ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  data-testid="dispo-cta"
                  className="group mt-9 inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
                  {c("clinique.dispo.ouvert.cta")}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </>
            ) : (
              <>
                <Meta>{c("clinique.dispo.meta")}</Meta>
                <h2 className="mt-6 font-serif text-3xl font-light leading-snug tracking-tight md:text-4xl">
                  {c("clinique.dispo.pause.t1")} <em className="text-clay">{c("clinique.dispo.pause.t2")}</em>
                </h2>
                <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-smoke md:text-base">
                  {c("clinique.dispo.pause.texte")}
                  {availability?.note && <span className="mt-2 block">{availability.note}</span>}
                </p>
                <a href={sexualisUrl} {...(links.sexualis ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  data-testid="dispo-cta"
                  className="group mt-9 inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
                  {c("clinique.dispo.pause.cta")}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </>
            )}
          </div>
        </FadeUp>
      </section>

      {/* 13. LIEN SEXUALIS */}
      <section data-testid="clinique-sexualis-section" className="border-t border-linen py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
          <FadeUp><Meta>{c("clinique.sexualis.meta")}</Meta></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
              {c("clinique.sexualis.t1")} <em className="text-clay">{c("clinique.sexualis.t2")}</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-smoke md:text-lg">
              {c("clinique.sexualis.p")}
            </p>
            <a href={sexualisUrl} {...(links.sexualis ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              data-testid="clinique-sexualis-cta"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
              {c("clinique.sexualis.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </FadeUp>
        </div>
      </section>
    </Page>
  );
}
